import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchInput } from './SearchInput';
import { LocationButton } from './LocationButton';
import { RadiusSlider } from './RadiusSlider';
import { useSearch } from '@/context/SearchContext';
import { useIsMobile } from '@/hooks/useMediaQuery';

interface SearchPanelProps {
  className?: string;
}

export function SearchPanel({ className }: SearchPanelProps) {
  const { hasSearched, locationName, isExpanded, setIsExpanded } = useSearch();
  const isMobile = useIsMobile();

  // Collapsed state - compact bar
  if (hasSearched && !isExpanded) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={clsx(
          'bg-white rounded-xl shadow-lg',
          isMobile ? 'mx-4 mt-4' : '',
          className
        )}
      >
        {/* Collapsed header - click to expand */}
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 rounded-xl transition-colors"
          data-track="search_expand"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
              <svg
                className="w-5 h-5 text-brand-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{locationName || 'Search location'}</p>
              <p className="text-sm text-gray-500">Tap to search again</p>
            </div>
          </div>
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>

        {/* Radius slider always visible in collapsed state on desktop */}
        {!isMobile && (
          <div className="px-4 pb-8 border-t border-gray-100">
            <RadiusSlider className="mt-3" />
          </div>
        )}
      </motion.div>
    );
  }

  // Expanded state - full search panel
  return (
    <>
      {/* Backdrop for mobile - also acts as centering container */}
      <AnimatePresence>
        {isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-panel flex items-center justify-center p-4"
            onClick={() => hasSearched && setIsExpanded(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 pb-8 space-y-5">
                {/* Close button for mobile when has searched */}
                {hasSearched && (
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}

                {/* Headline - only show before first search */}
                {!hasSearched && (
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-brand-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      Find the cheapest fuel near you
                    </h1>
                    <p className="mt-2 text-gray-600">
                      Compare prices at petrol stations across the UK
                    </p>
                  </div>
                )}

                {/* Search input */}
                <SearchInput />

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">or</span>
                  </div>
                </div>

                {/* Location button */}
                <LocationButton />

                {/* Radius slider - only show after first search on mobile */}
                {hasSearched && <RadiusSlider />}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop version */}
      {!isMobile && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={clsx('bg-white rounded-2xl shadow-2xl relative', className)}
        >
          <div className="p-6 pb-8 space-y-5">
            {/* Headline - only show before first search */}
            {!hasSearched && (
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Find the cheapest fuel near you
                </h1>
                <p className="text-gray-600">
                  Compare prices at petrol stations across the UK
                </p>
              </div>
            )}

            {/* Search input */}
            <SearchInput />

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Location button */}
            <LocationButton />

            {/* Radius slider */}
            <div className="pt-2">
              <RadiusSlider />
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}
